/**
 * @voltxampere/spice — AC source codec (V with SIN or AC clauses)
 *
 * Berkeley SPICE3 syntax (two forms):
 *   V{name} N+ N- SIN(VOFF VAMP FREQ TD DAMPING)
 *   V{name} N+ N- AC mag phase
 *
 * Examples:
 *   V1 in 0 SIN(0 1 1k 0 0)
 *   Vac in 0 AC 1 0
 *
 * Schema mapping: `type: "ac-source"`, params: `{ amplitude,
 * frequency, phase, offset?, delay?, damping? }`. AC keyword form
 * defaults frequency to 1 Hz (no transient meaning, only AC sweep).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ok, err, } from "./_base.js";
import { parseSpiceNumber, formatSpiceValue } from "../units.js";
import { numericParam, functionName, stripFunctionBody } from "./_param-helpers.js";
export const acSourceCodec = {
    spiceLetter: "V",
    schemaTypes: ["ac-source"],
    examples: ["V1 in 0 SIN(0 1 1k 0 0)", "Vac in 0 AC 1 0"],
    parse(card, _ctx) {
        if (card.tokens.length < 3) {
            return err("AC-V needs 2 nodes and an AC/SIN clause");
        }
        const [na, nb, ...rest] = card.tokens;
        if (na === undefined || nb === undefined) {
            return err("AC-V missing nodes");
        }
        const sinTok = rest.find((tok) => functionName(tok) === "SIN");
        if (sinTok !== undefined) {
            return parseSinForm(card, na, nb, sinTok);
        }
        const acIdx = rest.findIndex((tok) => tok.toUpperCase() === "AC");
        if (acIdx !== -1) {
            const magRaw = rest[acIdx + 1];
            const phaseRaw = rest[acIdx + 2];
            const mag = magRaw === undefined ? null : parseSpiceNumber(magRaw);
            const phase = phaseRaw === undefined ? 0 : (parseSpiceNumber(phaseRaw) ?? 0);
            if (mag === null)
                return err("AC keyword missing magnitude");
            const params = {
                amplitude: mag,
                frequency: 1,
                phase,
                offset: 0,
            };
            const component = {
                id: card.identifier,
                type: "ac-source",
                position: { x: 0, y: 0 },
                rotation: 0,
                flipped: false,
                label: card.identifier,
                params,
                terminals: [],
            };
            return ok(component, [na, nb]);
        }
        return err("AC-V needs SIN(...) or AC keyword");
    },
    emit(component, terminals) {
        const offset = numericParam(component.params.offset, 0);
        const amplitude = numericParam(component.params.amplitude, 1);
        const frequency = numericParam(component.params.frequency, 1000);
        const td = numericParam(component.params.delay, 0);
        const damping = numericParam(component.params.damping, 0);
        const [a, b] = terminals;
        const body = [offset, amplitude, frequency, td, damping].map(formatSpiceValue).join(" ");
        return `${component.id} ${a ?? "0"} ${b ?? "0"} SIN(${body})`;
    },
    owns(schemaType) {
        return schemaType === "ac-source";
    },
};
function parseSinForm(card, na, nb, sinTok) {
    const inner = stripFunctionBody(sinTok);
    if (inner === null)
        return err("SIN body shape mismatch");
    const parts = inner.split(/\s+/).filter((p) => p.length > 0);
    if (parts.length < 3) {
        return err(`SIN expects ≥3 parameters, got ${String(parts.length)}`);
    }
    const offset = parseSpiceNumber(parts[0] ?? "");
    const amplitude = parseSpiceNumber(parts[1] ?? "");
    const frequency = parseSpiceNumber(parts[2] ?? "");
    if (offset === null || amplitude === null || frequency === null) {
        return err("SIN required params (offset, amplitude, frequency) malformed");
    }
    const delay = parts[3] !== undefined ? (parseSpiceNumber(parts[3]) ?? 0) : 0;
    const damping = parts[4] !== undefined ? (parseSpiceNumber(parts[4]) ?? 0) : 0;
    const params = {
        amplitude,
        frequency,
        phase: 0,
        offset,
        delay,
        damping,
    };
    const component = {
        id: card.identifier,
        type: "ac-source",
        position: { x: 0, y: 0 },
        rotation: 0,
        flipped: false,
        label: card.identifier,
        params,
        terminals: [],
    };
    return ok(component, [na, nb]);
}
//# sourceMappingURL=ac-source.js.map